import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { crawlApi, siteApi } from "@/api/endpoints";
import {
  Button,
  Callout,
  Card,
  CardHeader,
  DetailLayout,
  DetailMain,
  DetailRail,
  Field,
  PageHeader,
} from "@/components/ui";
import {
  AlertIcon,
  CheckCircleIcon,
  GlobeIcon,
  InfoIcon,
  ShieldIcon,
} from "@/components/icons";
import { inputClass } from "@/utils/format";

/**
 * Website onboarding.
 *
 * Only name and URL are required. Business context genuinely improves the
 * analysis, but demanding it up front is the fastest way to lose someone before
 * they see any value, so everything else is optional and clearly marked.
 */
export function NewSitePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    base_url: "",
    target_country: "",
    business_description: "",
    products_services: "",
    target_keywords: "",
  });
  const [startCrawl, setStartCrawl] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const site = await siteApi.create({
        name: form.name.trim(),
        base_url: form.base_url.trim(),
        target_country: form.target_country.trim().toUpperCase() || null,
        business_description: form.business_description.trim() || null,
        products_services: splitList(form.products_services),
        target_keywords: splitList(form.target_keywords),
      });
      if (startCrawl) {
        // Best-effort: a failed crawl start must not lose the created site.
        try {
          await crawlApi.start(site.id, {});
        } catch {
          // The dashboard surfaces the "run a crawl" call to action instead.
        }
      }
      return site;
    },
    onSuccess: async (site) => {
      await queryClient.invalidateQueries({ queryKey: ["sites"] });
      navigate(`/sites/${site.id}`);
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError("Could not connect the website. Please try again.");
      }
    },
  });

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <>
      <PageHeader
        title="Connect a website"
        description="We will crawl the site and run a technical SEO audit. No integrations are required to begin."
      />

      <form onSubmit={handleSubmit} noValidate>
        <div className="onboarding-promise"><span><GlobeIcon /></span><div><strong>A clearer picture starts with your website.</strong><p>Add your details, set your business context, and let your first crawl build the baseline.</p></div><span className="onboarding-promise-label">CONNECT / DISCOVER / IMPROVE</span></div>
      <DetailLayout>
          <DetailMain>
            {error ? (
              <Callout tone="danger" icon={<AlertIcon className="h-5 w-5" />}>
                {error}
              </Callout>
            ) : null}

            <Card>
              <CardHeader
                title="Website"
                icon={<GlobeIcon className="h-4 w-4" />}
                description="The only details required to get started."
              />
              <div className="space-y-4">
                <Field
                  label="Website name"
                  htmlFor="name"
                  error={fieldErrors.name}
                  required
                >
                  <input
                    id="name"
                    required
                    value={form.name}
                    onChange={(event) => update("name", event.target.value)}
                    className={inputClass}
                    placeholder="Acme Accounting"
                  />
                </Field>

                <Field
                  label="Website URL"
                  htmlFor="base_url"
                  hint="The homepage. We crawl this origin only — https:// is assumed if omitted."
                  error={fieldErrors.base_url}
                  required
                >
                  <input
                    id="base_url"
                    required
                    value={form.base_url}
                    onChange={(event) => update("base_url", event.target.value)}
                    className={inputClass}
                    placeholder="https://www.acme.com"
                    inputMode="url"
                  />
                </Field>

                <Field
                  label="Target country"
                  htmlFor="target_country"
                  hint="Two-letter code, e.g. GB, US, KE. Optional."
                  error={fieldErrors.target_country}
                >
                  <input
                    id="target_country"
                    maxLength={2}
                    value={form.target_country}
                    onChange={(event) =>
                      update("target_country", event.target.value.toUpperCase())
                    }
                    className={`${inputClass} w-28 uppercase`}
                    placeholder="GB"
                  />
                </Field>
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Business context"
                icon={<InfoIcon className="h-4 w-4" />}
                description="Optional, but it materially improves prioritisation — it tells the analysis which pages actually matter commercially."
              />
              <div className="space-y-4">
                <Field
                  label="What does this business do?"
                  htmlFor="business_description"
                  hint="A few sentences is plenty."
                >
                  <textarea
                    id="business_description"
                    rows={3}
                    value={form.business_description}
                    onChange={(event) =>
                      update("business_description", event.target.value)
                    }
                    className={inputClass}
                    placeholder="Cloud accounting software for small businesses in East Africa."
                  />
                </Field>

                <Field
                  label="Products or services"
                  htmlFor="products_services"
                  hint="Comma separated."
                >
                  <input
                    id="products_services"
                    value={form.products_services}
                    onChange={(event) =>
                      update("products_services", event.target.value)
                    }
                    className={inputClass}
                    placeholder="accounting software, payroll, invoicing"
                  />
                </Field>

                <Field
                  label="Target keywords"
                  htmlFor="target_keywords"
                  hint="Comma separated. Leave blank and Search Console will supply the real ones once connected."
                >
                  <input
                    id="target_keywords"
                    value={form.target_keywords}
                    onChange={(event) =>
                      update("target_keywords", event.target.value)
                    }
                    className={inputClass}
                    placeholder="accounting software kenya, small business payroll"
                  />
                </Field>
              </div>
            </Card>

            <Card>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={startCrawl}
                  onChange={(event) => setStartCrawl(event.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
                />
                <span className="text-sm">
                  <span className="font-medium text-ink">
                    Start the first crawl immediately
                  </span>
                  <span className="mt-0.5 block text-ink-muted">
                    We respect robots.txt and crawl politely. Nothing on your
                    site is modified.
                  </span>
                </span>
              </label>
            </Card>

            <div className="flex gap-3">
              <Button
                type="submit"
                variant="primary"
                loading={mutation.isPending}
              >
                Connect website
              </Button>
              <Button variant="ghost" onClick={() => navigate("/sites")}>
                Cancel
              </Button>
            </div>
          </DetailMain>

          <DetailRail>
            <Card>
              <CardHeader
                title="What happens next"
                icon={<CheckCircleIcon className="h-4 w-4" />}
              />
              <ol className="space-y-3.5">
                {NEXT_STEPS.map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="tnum grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-subtle text-xs font-semibold text-brand">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink">
                        {step.title}
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
                        {step.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>

            <Card className="border-info/25 bg-info-subtle">
              <div className="flex gap-3">
                <ShieldIcon className="h-5 w-5 shrink-0 text-info" />
                <div>
                  <p className="text-sm font-medium text-ink">
                    Nothing is changed
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                    Connecting a website only reads it. Any change SEO RankFix
                    later proposes waits for your explicit approval before it is
                    applied.
                  </p>
                </div>
              </div>
            </Card>
          </DetailRail>
        </DetailLayout>
      </form>
    </>
  );
}

/** The loop, stated up front so a first-time user knows what they are starting. */
const NEXT_STEPS = [
  {
    title: "We crawl the site",
    body: "Politely, respecting robots.txt, collecting the SEO signals from every page we find.",
  },
  {
    title: "We audit what we found",
    body: "Deterministic checks turn the crawl into findings, each with the evidence behind it.",
  },
  {
    title: "You get a ranked list",
    body: "Recommendations scored by impact, confidence, effort and risk — with the score broken out.",
  },
  {
    title: "You approve, we verify",
    body: "Approved changes are applied, then the live page is re-fetched to confirm they landed.",
  },
];

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 100);
}
